import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import StudentLayout from "@/components/StudentLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sunrise, Sunset } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  } catch { return iso; }
};

function groupByDay(items) {
  const map = {};
  for (const r of items) {
    if (!map[r.date]) map[r.date] = { date: r.date, morning: null, afternoon: null };
    const s = (r.session || "morning").toLowerCase();
    if (s.startsWith("after") || s.startsWith("even") || s.startsWith("noon")) map[r.date].afternoon = r.status;
    else map[r.date].morning = r.status;
  }
  return Object.values(map).sort((a, b) => (a.date < b.date ? 1 : -1));
}

export default function StudentAttendance() {
  const [days, setDays] = useState(60);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/student/attendance", { params: { days } });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) toast.error(apiErr(e.response?.data?.detail) || "Could not load attendance");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [days]);

  return (
    <StudentLayout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">My attendance</div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100" style={{fontFamily:"Outfit"}}>Attendance history</h1>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
          <SelectTrigger className="h-11 rounded-xl w-44" data-testid="student-att-days"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="60">Last 60 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="180">Last 180 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-10 text-center text-slate-500">Loading…</div>
      ) : !data || data.items.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-10 text-center text-slate-500" data-testid="att-empty">No attendance records in this window.</div>
      ) : (
        <>
          <div className="grid sm:grid-cols-4 gap-3 mb-5" data-testid="att-summary">
            <Pill label="Total days" value={data.summary.total_days} tone="slate" sub={`${data.summary.total} sessions`}/>
            <Pill label="Present" value={data.summary.present_days} tone="emerald" sub={`${data.summary.present} sessions`}/>
            <Pill label="Absent" value={data.summary.absent_days} tone="rose" sub={`${data.summary.absent} sessions`}/>
            <Pill label="Percentage" value={`${data.summary.percentage}%`} tone="sky" sub="½ day per session"/>
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="att-table">
            <div className="hidden sm:grid grid-cols-[1fr_1fr_1fr] gap-3 px-4 py-3 bg-slate-50 dark:bg-slate-700 text-slate-500 text-xs uppercase tracking-[0.15em]">
              <div>Date</div>
              <div>Morning</div>
              <div>Afternoon</div>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-slate-700">
              {groupByDay(data.items).map((day) => (
                <div key={day.date} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr] gap-2 sm:gap-3 px-4 py-3 items-center" data-testid={`att-day-${day.date}`}>
                  <div className="font-medium text-slate-700 dark:text-slate-200">{fmtDate(day.date)}</div>
                  <SessionCell kind="morning" status={day.morning} date={day.date} />
                  <SessionCell kind="afternoon" status={day.afternoon} date={day.date} />
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </StudentLayout>
  );
}

function SessionCell({ kind, status, date }) {
  const isMorning = kind === "morning";
  const Icon = isMorning ? Sunrise : Sunset;
  // subtle per-session accent (small distinguish): amber for morning, indigo for afternoon
  const accent = isMorning
    ? "text-amber-500 border-l-2 border-amber-300 dark:border-amber-500/50"
    : "text-indigo-500 border-l-2 border-indigo-300 dark:border-indigo-500/50";
  const badge = status === "present"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-700"
    : status === "absent"
      ? "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-700"
      : "bg-slate-50 text-slate-400 border-slate-200 dark:bg-slate-800 dark:border-slate-700";
  return (
    <div className={`flex items-center gap-2 pl-2.5 ${accent}`} data-testid={`att-session-${date}-${kind}`}>
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="text-[11px] uppercase tracking-wide text-slate-400 w-16 sm:hidden">{isMorning ? "Morning" : "Afternoon"}</span>
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${badge}`}>
        {status === "present" ? "Present" : status === "absent" ? "Absent" : "—"}
      </span>
    </div>
  );
}

function Pill({ label, value, tone, sub }) {
  const map = {
    slate: "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200",
    sky: "bg-sky-50 dark:bg-sky-950/40 border-sky-200 dark:border-sky-700 text-sky-700 dark:text-sky-300",
    rose: "bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-700 text-rose-700 dark:text-rose-300",
    emerald: "bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300",
  };
  return (
    <div className={`rounded-2xl border p-4 ${map[tone]}`}>
      <div className="text-xs uppercase tracking-[0.2em] opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{fontFamily:"Outfit"}}>{value}</div>
      {sub && <div className="text-[11px] opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}
